import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { getDrawdownLoansBySearch } from "../../../actions/loan.action";
import Badge from "../../../components/badge";
import { Select } from "../../../components/input-functions";
import Pagination from "../../../components/pagination";
import Table, { TableColumns } from "../../../components/table";
import { DrawdownLoan, DrawdownLoans } from "../../../dto";
import useQueryParams from "../../../hooks/useQueryParams";
import dispatch from "../../../middleware";
import {
  LoanStatusEnum,
  RetailODDrawdownStatusEnum,
  RoutePaths,
} from "../../../models";
import { ReduxState } from "../../../reducers";
import {
  clearFormData,
  dateToDDMMYYYY,
  isLoadingActive,
  splitKeyWithSpace,
} from "../../../utils";
import { DrawdownLoanStatus } from "../../enterprise-loans/types";

function getIdLink(path: RoutePaths, id?: string) {
  if (!id) return;
  const navigationLink = `${path}/${id}`;
  return (
    <Link to={navigationLink} className="table-link">
      {id}
    </Link>
  );
}

function StatusBadge({ status }: { status: LoanStatusEnum }) {
  if ([LoanStatusEnum.DISBURSED, LoanStatusEnum.APPROVED].includes(status))
    return <Badge type="success">{status}</Badge>;
  if (status === LoanStatusEnum.OVERDUE)
    return <Badge type="reject">{status}</Badge>;
  return <Badge type="pending">{splitKeyWithSpace(status)}</Badge>;
}

const COLUMNS_FOR_DRAWDOWN_LOANS: TableColumns[] = [
  { label: "Drawdown ID", field: "_id", style: { textAlign: "center" } },
  {
    label: "Amount",
    field: "amount",
    style: { textAlign: "center" },
  },
  {
    label: "Tenor (months)",
    field: "tenureInMonths",
    style: { textAlign: "center" },
  },
  {
    label: "Date",
    field: "sanctionedAt",
    style: { textAlign: "center" },
  },
  {
    label: "Status",
    field: "status",
    style: { textAlign: "center" },
  },
];

function getDataForDrawdownLoans(
  drawdownLoan: DrawdownLoan[] | undefined
): Array<
  Omit<DrawdownLoan, "status" | "statusLogs" | "_id"> & {
    status: JSX.Element;
    createdAt: string;
    _id: JSX.Element | string;
    sanctionedAt: string | undefined;
  }
> {
  if (!drawdownLoan) return [];

  return drawdownLoan.map(({ statusLogs, ...rest }) => ({
    ...rest,
    _id: getIdLink(RoutePaths.DRAWDOWN_DETAILS, rest._id) ?? rest._id,
    status: <StatusBadge status={rest.status} />,
    sanctionedAt: dateToDDMMYYYY(
      statusLogs.find(
        ({ status }) => status === RetailODDrawdownStatusEnum.INITIATED
      )?.updatedAt
    ),
  }));
}

type DrawdownSearchQuery = {
  pageNo: number;
  numberOfEntitiesPerPage: number;
  status: string;
};

const DEFAULT_DRAWDOWN_SEARCH_QUERY_STATE: DrawdownSearchQuery = {
  pageNo: 1,
  numberOfEntitiesPerPage: 10,
  status: "",
};

interface DrawdownContainerProps {
  loanType: string;
}

export default function DrawdownContainer({
  loanType,
}: DrawdownContainerProps) {
  const { loanId }: { loanId: string } = useParams();
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.loan.loadingQueue
  );
  const drawdownLoans: DrawdownLoans | null =
    useSelector((state: ReduxState) => state.loan.drawdownLoans) ?? null;
  const drawdownLoan: DrawdownLoan[] | undefined = drawdownLoans?.data;

  const drawdownLoanPageNumberLimit =
    drawdownLoans?.metadata.pageNumberLimit ?? 0;

  const isLoading = isLoadingActive(loadingQueue);
  const [{ pageNo, numberOfEntitiesPerPage, status }, setSearchQuery] =
    useQueryParams<DrawdownSearchQuery>(DEFAULT_DRAWDOWN_SEARCH_QUERY_STATE);

  const tableDataForDrawdownLoans = useMemo(() => {
    if (!drawdownLoan) return [];
    return getDataForDrawdownLoans(drawdownLoan);
  }, [drawdownLoan]);

  useEffect(() => {
    if (!loanId) return;
    dispatch(
      storeDispatch,
      getDrawdownLoansBySearch(
        clearFormData({ numberOfEntitiesPerPage, pageNo, loanId, status })
      )
    );
  }, [loanId, numberOfEntitiesPerPage, pageNo, status, storeDispatch]);

  return (
    <div className="dashboard-container">
      <div className="w-25 mb-3">
        <Select
          name="Status"
          options={Object.keys(DrawdownLoanStatus).map((key) => ({
            label: splitKeyWithSpace(key),
            value: key,
          }))}
          value={status}
          placeholder="Select Status"
          onChange={(value) =>
            setSearchQuery((prevState) => ({
              ...prevState,
              status: value.target.value,
              pageNo: 1,
            }))
          }
        />
      </div>
      <Table
        loading={isLoading}
        data={tableDataForDrawdownLoans}
        columns={COLUMNS_FOR_DRAWDOWN_LOANS}
      />
      <Pagination
        currentPage={pageNo}
        totalEntries={drawdownLoanPageNumberLimit * numberOfEntitiesPerPage}
        numberOfEntriesPerPage={numberOfEntitiesPerPage}
        onPageChange={(pageNo) =>
          setSearchQuery((prevState) => ({
            ...prevState,
            pageNo,
          }))
        }
        onPerPageCountChange={(countPerPage) =>
          setSearchQuery((prevState) => ({
            ...prevState,
            numberOfEntitiesPerPage: countPerPage,
            pageNo: 1,
          }))
        }
      />
    </div>
  );
}
